import { motion } from 'framer-motion';

// SGA STRAT Brand Motif - Green dot-arrow element
// Based on the logo's distinctive upward-pointing green dots

export const BrandDot = ({ className = "", size = "sm" }) => {
  const sizes = {
    xs: "w-1.5 h-1.5",
    sm: "w-2 h-2",
    md: "w-3 h-3",
    lg: "w-4 h-4",
  };
  
  return (
    <span 
      className={`inline-block rounded-full bg-[#4DB848] ${sizes[size]} ${className}`}
    />
  );
};

// Brand Arrow Motif - Upward pointing dots (like in logo)
export const BrandArrowUp = ({ className = "", size = 24 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    className={className}
  >
    {/* Top dot */}
    <circle cx="12" cy="4" r="2.5" fill="#4DB848" />
    {/* Middle row - 2 dots */}
    <circle cx="8" cy="10" r="2.5" fill="#4DB848" />
    <circle cx="16" cy="10" r="2.5" fill="#4DB848" />
    {/* Bottom row - 3 dots */}
    <circle cx="4" cy="16" r="2.5" fill="#4DB848" />
    <circle cx="12" cy="16" r="2.5" fill="#4DB848" />
    <circle cx="20" cy="16" r="2.5" fill="#4DB848" />
  </svg>
);

// Brand Arrow Right - For flow/process indicators
export const BrandArrowRight = ({ className = "", size = 24 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    className={className}
  >
    {/* Left column - 3 dots */}
    <circle cx="4" cy="4" r="2" fill="#4DB848" />
    <circle cx="4" cy="12" r="2" fill="#4DB848" />
    <circle cx="4" cy="20" r="2" fill="#4DB848" />
    {/* Middle column - 2 dots */}
    <circle cx="12" cy="8" r="2" fill="#4DB848" />
    <circle cx="12" cy="16" r="2" fill="#4DB848" />
    {/* Right - 1 dot (arrow point) */}
    <circle cx="20" cy="12" r="2.5" fill="#4DB848" />
  </svg>
);

// Animated Flow Arrow - For BUILD → OPERATE → ORCHESTRATE
export const AnimatedFlowArrow = ({ 
  direction = "right", 
  className = "",
  delay = 0,
  duration = 0.8
}) => {
  const dots = direction === "right" 
    ? [
        { cx: 0, cy: 12, r: 3, delay: 0 },
        { cx: 12, cy: 12, r: 3, delay: 0.1 },
        { cx: 24, cy: 12, r: 3, delay: 0.2 },
        { cx: 36, cy: 12, r: 4, delay: 0.3 },
      ]
    : [
        { cx: 12, cy: 0, r: 3, delay: 0 },
        { cx: 12, cy: 12, r: 3, delay: 0.1 },
        { cx: 12, cy: 24, r: 3, delay: 0.2 },
        { cx: 12, cy: 36, r: 4, delay: 0.3 },
      ];

  return (
    <svg 
      width={direction === "right" ? 44 : 24} 
      height={direction === "right" ? 24 : 44} 
      viewBox={direction === "right" ? "0 0 44 24" : "0 0 24 44"} 
      fill="none" 
      className={className}
    >
      {dots.map((dot, index) => (
        <motion.circle
          key={index}
          cx={dot.cx}
          cy={dot.cy}
          r={dot.r}
          fill="#4DB848"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            delay: delay + dot.delay,
            duration: duration * 0.3,
            ease: "easeOut"
          }}
        />
      ))}
    </svg>
  );
};

// Section Divider with brand motif
export const BrandDivider = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-2 ${className}`}>
    <div className="h-px w-12 bg-slate-200" />
    <BrandDot size="xs" />
    <BrandDot size="sm" />
    <BrandDot size="xs" />
    <div className="h-px w-12 bg-slate-200" />
  </div>
);

// Bullet point with brand dot
export const BrandBullet = ({ children, className = "" }) => (
  <li className={`flex items-start gap-3 ${className}`}>
    <span className="w-2 h-2 rounded-full bg-[#4DB848] mt-2 flex-shrink-0" />
    <span>{children}</span>
  </li>
);

// CTA Arrow indicator
export const CTAArrow = ({ className = "" }) => (
  <motion.span 
    className={`inline-flex items-center gap-1 ${className}`}
    whileHover={{ x: 4 }}
    transition={{ duration: 0.2 }}
  >
    <BrandDot size="xs" />
    <BrandDot size="xs" />
    <BrandDot size="sm" />
  </motion.span>
);

// Hover card accent - appears on hover
export const CardHoverAccent = ({ isHovered = false }) => (
  <motion.div
    className="absolute bottom-0 left-0 right-0 h-1 flex justify-center items-center gap-1"
    initial={{ opacity: 0 }}
    animate={{ opacity: isHovered ? 1 : 0 }}
    transition={{ duration: 0.3 }}
  >
    <motion.span 
      className="w-1.5 h-1.5 rounded-full bg-[#4DB848]"
      initial={{ scale: 0 }}
      animate={{ scale: isHovered ? 1 : 0 }}
      transition={{ delay: 0 }}
    />
    <motion.span 
      className="w-2 h-2 rounded-full bg-[#4DB848]"
      initial={{ scale: 0 }}
      animate={{ scale: isHovered ? 1 : 0 }}
      transition={{ delay: 0.05 }}
    />
    <motion.span 
      className="w-2.5 h-2.5 rounded-full bg-[#4DB848]"
      initial={{ scale: 0 }}
      animate={{ scale: isHovered ? 1 : 0 }}
      transition={{ delay: 0.1 }}
    />
    <motion.span 
      className="w-2 h-2 rounded-full bg-[#4DB848]"
      initial={{ scale: 0 }}
      animate={{ scale: isHovered ? 1 : 0 }}
      transition={{ delay: 0.15 }}
    />
    <motion.span 
      className="w-1.5 h-1.5 rounded-full bg-[#4DB848]"
      initial={{ scale: 0 }}
      animate={{ scale: isHovered ? 1 : 0 }}
      transition={{ delay: 0.2 }}
    />
  </motion.div>
);

// Process Flow Connector - Animated arrow between steps
export const ProcessFlowConnector = ({ 
  isVisible = false, 
  direction = "horizontal",
  className = "" 
}) => {
  const isHorizontal = direction === "horizontal";
  
  return (
    <div className={`flex ${isHorizontal ? 'flex-row items-center' : 'flex-col items-center'} ${className}`}>
      {[0, 1, 2, 3, 4].map((index) => (
        <motion.div
          key={index}
          className="rounded-full bg-[#4DB848]"
          style={{
            width: index === 4 ? 12 : 8,
            height: index === 4 ? 12 : 8,
            margin: isHorizontal ? '0 4px' : '4px 0',
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: isVisible ? 1 : 0, 
            scale: isVisible ? 1 : 0 
          }}
          transition={{ 
            delay: index * 0.1,
            duration: 0.3,
            ease: "easeOut"
          }}
        />
      ))}
    </div>
  );
};
